import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Code, 
  Server, 
  Database, 
  Cloud,
  Brain
} from 'lucide-react';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const skillCategories = [
    {
      title: "Frontend",
      icon: Code,
      color: "from-blue-500 to-cyan-500",
      skills: [
        { name: "React.js", level: 60 },
        { name: "Vue.js", level: 50 },
        { name: "Ejs", level: 80 },
        { name: "CSS", level: 80 },
        { name: "JavaScript", level: 95 }
      ]
    },
    {
      title: "Backend",
      icon: Server,
      color: "from-green-500 to-emerald-500",
      skills: [
        { name: "Node.js", level: 90 },
        { name: "Python", level: 80 },
        { name: "Express.js", level: 90 },
        { name: "Django", level: 70 },
        { name: "FastAPI", level: 70 }
      ]
    },
    {
      title: "Database",
      icon: Database,
      color: "from-purple-500 to-pink-500",
      skills: [
        { name: "PostgreSQL", level: 70 },
        { name: "MongoDB", level: 90 },
        { name: "MySQL", level: 80 },
        { name: "Redis", level: 75 },
        { name: "Prisma", level: 10 }
      ]
    },
    {
      title: "DevOps & Tools",
      icon: Cloud,
      color: "from-orange-500 to-red-500",
      skills: [
        { name: "Docker", level: 70 },
        { name: "Git", level: 90 },
        { name: "AWS", level: 10 },
        { name: "CI/CD", level: 60 },
        { name: "Linux", level: 95 }
      ]
    },
    {
      title: "Machine Learning",
      icon: Brain,
      color: "from-orange-500 to-red-500",
      skills: [
        { name: "CNN", level: 70 },
        { name: "MLP", level: 90 },
        { name: "RNN", level: 70 },
        { name: "Tensorflow", level: 70 },
        { name: "GAN", level: 60 }
      ]
    }
  ];

  const ProgressBar = ({ level, inView }) => (
    <motion.div
      className="h-2 bg-gray-200 rounded-full overflow-hidden"
      initial={{ width: 0 }}
      animate={{ width: inView ? "100%" : 0 }}
      transition={{ duration: 1, delay: 0.5 }}
    >
      <motion.div
        className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full"
        initial={{ width: 0 }}
        animate={{ width: inView ? `${level}%` : 0 }}
        transition={{ duration: 1.5, delay: 0.7 }}
      />
    </motion.div>
  );

  return (
    <section id="skills" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2
            variants={itemVariants}
            className="section-title text-center mb-16"
          >
            Compétences Techniques
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skillCategories.map((category, index) => (
              <motion.div
                key={category.title}
                variants={itemVariants}
                className="card group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-center mb-6">
                  <motion.div
                    className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-full flex items-center justify-center text-white`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <category.icon size={28} />
                  </motion.div>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
                  {category.title}
                </h3>

                <div className="space-y-4">
                  {category.skills.map((skill, skillIndex) => (
                    <div key={skill.name} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">
                          {skill.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {skill.level}%
                        </span>
                      </div>
                      <ProgressBar level={skill.level} inView={inView} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            variants={itemVariants}
            className="mt-16 text-center"
          >
            <div className="inline-flex flex-wrap justify-center gap-3">
              {[
                "HTML5", "CSS3", "JavaScript", "React", "Vue.js", "Node.js",
                "Python", "PHP", "TypeScript", "PostgreSQL","MySQL","MongoDB", "Docker",
                "Git",  "REST API", 
              ].map((tech, index) => (
                <motion.span
                  key={tech}
                  className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-md hover:shadow-lg transition-shadow cursor-default"
                  whileHover={{ scale: 1.05, y: -2 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: inView ? 1 : 0, 
                    scale: inView ? 1 : 0.8 
                  }}
                  transition={{ 
                    delay: 0.1 * index,
                    duration: 0.5
                  }}
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="mt-12 text-center"
          >
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Je suis passionné par l'apprentissage continu et je reste toujours 
              à jour avec les dernières technologies et meilleures pratiques 
              de l'industrie du développement web.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
